import { Link } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { SEO } from "../components/SEO";
import { mockServices } from "../mock";
import { ArrowRight } from "lucide-react";

export const Services = () => {
  return (
    <div className="min-h-screen pt-20">
      <SEO
        title="Outdoor Living Services | Pergolas, Glass Verandas & Blinds | Hudson Knox"
        description="Complete range of outdoor living services. Motorised pergolas, glass verandas, pergola blinds, rooflight blinds & plantation shutters. Premium installations across South East England."
        keywords="outdoor living services, pergola services, glass veranda installation, outdoor blinds, rooflight blinds, plantation shutters, outdoor structures"
        canonicalPath="/services"
      />
      {/* Hero */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden bg-neutral-900">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-light text-white mb-4">Our Services</h1>
          <p className="text-xl text-neutral-200">Premium outdoor living solutions for your home</p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-light text-neutral-900 mb-6">Luxury Outdoor Living</h2>
          <p className="text-lg text-neutral-600 leading-relaxed">
            Hudson Knox Outdoor Living specialises in designing and installing premium outdoor structures 
            and light-control systems. From motorised pergolas to elegant glass verandas, we create 
            bespoke solutions that transform your outdoor space into a sophisticated living area.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {mockServices.map((service) => (
              <Card
                key={service.id}
                className="border-neutral-200 hover:shadow-2xl transition-all duration-300 group"
              >
                <CardContent className="p-8">
                  <div className="w-14 h-14 bg-neutral-900 rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                    <div className="w-7 h-7 bg-white rounded-sm" />
                  </div>
                  <h3 className="text-2xl font-medium text-neutral-900 mb-4">{service.title}</h3>
                  <p className="text-neutral-600 mb-6 leading-relaxed">{service.shortDescription}</p>
                  <Link to={`/services/${service.slug}`}>
                    <Button
                      variant="ghost"
                      className="p-0 h-auto font-medium text-neutral-900 hover:bg-transparent group-hover:gap-2 transition-all"
                    >
                      Learn More <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-light text-neutral-900 mb-12 text-center">Why Choose Hudson Knox</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                title: "Expert Design",
                description: "Bespoke designs tailored to your property and lifestyle"
              },
              {
                title: "Premium Materials",
                description: "High-quality aluminium and glass with long warranties"
              },
              {
                title: "Professional Installation",
                description: "Experienced team delivering exceptional craftsmanship"
              },
              {
                title: "Ongoing Support",
                description: "Comprehensive aftercare and maintenance services"
              }
            ].map((benefit, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-neutral-900 rounded-full mx-auto mb-4 flex items-center justify-center">
                  <div className="w-8 h-8 border-2 border-white rounded-full" />
                </div>
                <h3 className="text-xl font-medium text-neutral-900 mb-3">{benefit.title}</h3>
                <p className="text-neutral-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-20 bg-neutral-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-light mb-6">Serving London & Kent</h2>
          <p className="text-xl text-neutral-300 mb-8">
            Professional outdoor living installations across the South East of England
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-neutral-900 hover:bg-neutral-100">
                Request Free Quote
              </Button>
            </Link>
            <Link to="/projects">
              <Button
                size="lg"
                variant="outline"
                className="border-white text-white hover:bg-white hover:text-neutral-900"
              >
                View Our Work
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
